import { ArrowUpDown, ChevronDown } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatPhoneNumber, hasPaidThisMonth } from '@/lib/utils';

export default function StudentsDataTable({ data, loading, children }) {
  const history = useNavigate();

  const [sorting, setSorting] = React.useState([]);
  const [columnFilters, setColumnFilters] = React.useState([]);
  const [columnVisibility, setColumnVisibility] = React.useState({});
  const [rowSelection, setRowSelection] = React.useState({});

  const columns = [
    {
      id: 'rowNumber',
      header: '#',
      cell: ({ row }) => (
        <div className="whitespace-nowrap">{row.index + 1}</div>
      ),
    },
    {
      accessorKey: 'fullName',
      header: ({ column }) => (
        <Button
          className="pl-0"
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Ism familiya
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="capitalize whitespace-nowrap">
          {row.getValue('fullName')}
        </div>
      ),
    },
    {
      accessorKey: 'isPay',
      header: "To'lov qilganligi",
      cell: ({ row }) => {
        const paidThisMonth = hasPaidThisMonth(row.original.paymentHistory);
        return (
          <div
            className={`${
              paidThisMonth ? 'text-green-500' : 'text-red-500'
            } whitespace-nowrap`}
          >
            {paidThisMonth ? "To'lov qilgan" : "To'lov qilmagan"}
          </div>
        );
      },
    },
    {
      accessorKey: 'address',
      header: 'Manzil',
      cell: ({ row }) => {
        const value = row.getValue('address');
        return (
          <div className="truncate whitespace-nowrap">
            {value ? value : 'Manzil kiritilmagan'}
          </div>
        );
      },
    },
    {
      accessorKey: 'degree',
      header: 'Darajasi',
      cell: ({ row }) => {
        const value = row.getValue('degree');
        return (
          <div className="whitespace-nowrap">
            {value ? formatPhoneNumber(value) : 'Daraja kiritilmagan'}
          </div>
        );
      },
    },
    {
      accessorKey: 'phoneNumber',
      header: 'Telefon raqam',
      cell: ({ row }) => {
        const value = row.getValue('phoneNumber');
        return (
          <div className="whitespace-nowrap">
            {value ? formatPhoneNumber(value) : 'Raqam kiritilmagan'}
          </div>
        );
      },
    },
    {
      accessorKey: 'parentPhoneNumber',
      header: 'Ota-onasini raqami',
      cell: ({ row }) => {
        const value = row.getValue('parentPhoneNumber');
        return (
          <div className="whitespace-nowrap">
            {value ? formatPhoneNumber(value) : 'Raqam kiritilmagan'}
          </div>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  return (
    <div className="w-full">
      {/* Filter va Column toggle qismi */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4 py-2">
        <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
          <Input
            placeholder="Ism bo‘yicha qidirish..."
            value={table.getColumn('fullName')?.getFilterValue() ?? ''}
            onChange={(event) =>
              table.getColumn('fullName')?.setFilterValue(event.target.value)
            }
            className="w-full sm:w-64"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full sm:w-auto">
                Ustunlar <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {column.id}
                  </DropdownMenuCheckboxItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div>{children}</div>
      </div>

      {/* Jadval scroll uchun wrap */}
      <div className="border rounded-md overflow-x-auto">
        <Table className="min-w-[700px]">
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead className="whitespace-nowrap" key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className="cursor-pointer"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-16 text-center"
                >
                  {loading ? 'Yuklanmoqda...' : "Ma'lumot topilmadi"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-2 py-4">
        <div className="flex-1 text-sm text-muted-foreground text-center sm:text-left hidden sm:block">
          {table.getFilteredRowModel().rows.length} ta natija
        </div>
        <div className="flex w-full sm:w-auto justify-center sm:justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full sm:w-auto"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Orqaga
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="w-full sm:w-auto"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Oldinga
          </Button>
        </div>
      </div>
    </div>
  );
}
